import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import "./operationtheatre.scss";
import { HMS_IFRAME_MESSAGES } from "./iframeMessages";
import { patientid } from "../../Redux/Slice/rootSlice";

const OT_URL =
  process.env.REACT_APP_OT_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:5173/ot" : "/ot-module");

const OT_ORIGIN = OT_URL.startsWith("http")
  ? new URL(OT_URL).origin
  : window.location.origin;

function parseStoredUser(userdata) {
  if (!userdata) return null;
  try {
    return JSON.parse(userdata);
  } catch (e) {
    return null;
  }
}

function OperationTheatre() {
  const iframeRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sideactive = useSelector((state) => state.counter.sidebarnav);
  const [isLoading, setIsLoading] = useState(true);
  const isNavFromIframeRef = useRef(false);

  // Compute initial iframe src once on mount to keep iframe window alive across route changes
  const [initialIframeSrc] = useState(() => {
    let base = OT_URL.replace(/\/ot.*$/, "");
    if (!base && OT_URL.startsWith("/")) {
      base = OT_URL.replace(/\/+$/, "");
    }
    let path = location.pathname;
    if (!path.startsWith("/ot")) {
      path = "/ot";
    }
    return `${base}${path}${location.search}`;
  });

  // Post route changes from HMS_Web shell to the embedded portal without reloading the iframe
  useEffect(() => {
    if (isNavFromIframeRef.current) {
      isNavFromIframeRef.current = false;
      return;
    }

    const iframe = iframeRef.current;
    if (iframe?.contentWindow) {
      try {
        iframe.contentWindow.postMessage(
          {
            type: "HMS_SHELL_NAVIGATE",
            path: location.pathname,
            search: location.search,
          },
          OT_ORIGIN
        );
      } catch (e) {}
    }
  }, [location.pathname, location.search]);

  const sendAuthToIframe = () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return false;

    const token = localStorage.getItem("token");
    const userdata = localStorage.getItem("userdata");
    const role = localStorage.getItem("role") || parseStoredUser(userdata)?.role || "";
    const branch = localStorage.getItem("selectedBranch") || localStorage.getItem("branch");
    if (!token) return false;

    try {
      const authPayload = {
        type: "HMS_AUTH_TOKEN",
        token,
        user: parseStoredUser(userdata),
        role,
        branch,
      };
      iframe.contentWindow.postMessage(authPayload, OT_ORIGIN);
      iframe.contentWindow.postMessage({ ...authPayload, type: "HMS_INIT_AUTH" }, OT_ORIGIN);
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.origin === OT_ORIGIN &&
        event.source === iframeRef.current?.contentWindow
      ) {
        if (event.data?.type === HMS_IFRAME_MESSAGES.ready || event.data?.type === "PROCEDURE_READY") {
          sendAuthToIframe();
          setIsLoading(false);
        } else if (event.data?.type === HMS_IFRAME_MESSAGES.authRequest || event.data?.type === "PROCEDURE_AUTH_REQUEST") {
          sendAuthToIframe();
        } else if ((event.data?.type === HMS_IFRAME_MESSAGES.navigate || event.data?.type === "HMS_NAVIGATE") && event.data?.path) {
          if (event.data?.patientId) {
            dispatch(patientid(event.data.patientId));
            localStorage.setItem("patientid", event.data.patientId);
          }
          if (location.pathname !== event.data.path) {
            isNavFromIframeRef.current = true;
            navigate(event.data.path);
          }
        } else if (event.data?.type === "HMS_UNAUTHORIZED") {
          localStorage.clear();
          window.location.href = "/login";
        }
      }
    };

    window.addEventListener("message", handleMessage);

    const fallbackTimer = setTimeout(() => {
      sendAuthToIframe();
      setIsLoading(false);
    }, 2500);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(fallbackTimer);
    };
  }, [navigate, location.pathname]);

  return (
    <div className={`ot-container ${sideactive ? "sidebar-open" : "sidebar-closed"}`}>
      {isLoading && (
        <div className="ot-loader" role="status" aria-live="polite">
          <div className="ot-loader-card">
            <span className="ot-loader-spinner" aria-hidden="true" />
            <strong>Loading Operation Theatre (OT)...</strong>
            <span>Syncing surgical suites, theatre schedules, and clinical staff rosters.</span>
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={initialIframeSrc}
        title="Operation Theatre (OT) Management"
        className="ot-iframe"
        onLoad={sendAuthToIframe}
      />
    </div>
  );
}

export default OperationTheatre;
